import { Helmet } from 'react-helmet-async';

const DEFAULT_TITLE = 'Raflr';
const DEFAULT_DESCRIPTION = 'Win amazing deals on electronics, gaming, fashion & more. Enter skill-based competitions, collect locally in Ireland. Secure payments, protected by escrow.';
const SITE_URL = 'https://raflr.ie';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * SEO Component - Dynamic meta tags for each page
 *
 * @param {string} title - Page title (will be appended with " | Raflr")
 * @param {string} description - Meta description (max 160 chars recommended)
 * @param {string} path - URL path (e.g., "/browse", "/competition/abc123")
 * @param {string} image - OG image URL (defaults to site og-image.png)
 * @param {string} type - OG type (default: "website", use "product" for competitions)
 * @param {boolean} noIndex - Set true to prevent indexing (for auth pages, etc.)
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noIndex = false,
}) {
  const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const canonicalUrl = `${SITE_URL}${path}`;

  // Truncate description to 160 chars if needed
  const metaDescription = description.length > 160
    ? description.slice(0, 157) + '...'
    : description;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={DEFAULT_TITLE} />
      <meta property="og:locale" content="en_IE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

/**
 * SEO for competition/product pages with structured data
 */
export function CompetitionSEO({ competition, path }) {
  const { title, description, photos = [], ticketPrice, ticketQuantity, ticketsSold, pickupArea } = competition;

  const image = photos[0] || DEFAULT_IMAGE;
  const priceInEuros = (ticketPrice / 100).toFixed(2);
  const availability = ticketsSold >= ticketQuantity ? 'SoldOut' : 'InStock';

  // Create a compelling description
  const metaDescription = description
    ? `${description.slice(0, 100)}... Entry from €${priceInEuros}. Local pickup in ${pickupArea || 'Ireland'}.`
    : `Win ${title} for just €${priceInEuros} per entry. Skill-based competition with local pickup in ${pickupArea || 'Ireland'}.`;

  // Product structured data for rich snippets
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: title,
    description: description?.slice(0, 500),
    image: photos,
    offers: {
      '@type': 'Offer',
      price: priceInEuros,
      priceCurrency: 'EUR',
      availability: `https://schema.org/${availability}`,
      url: `${SITE_URL}${path}`,
    },
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{`${title} | Raflr`}</title>
        <meta name="title" content={`${title} | Raflr`} />
        <meta name="description" content={metaDescription.slice(0, 160)} />
        <link rel="canonical" href={`${SITE_URL}${path}`} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`${SITE_URL}${path}`} />
        <meta property="og:title" content={`${title} | Raflr`} />
        <meta property="og:description" content={metaDescription.slice(0, 160)} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content="Raflr" />
        <meta property="og:locale" content="en_IE" />
        <meta property="product:price:amount" content={priceInEuros} />
        <meta property="product:price:currency" content="EUR" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} | Raflr`} />
        <meta name="twitter:description" content={metaDescription.slice(0, 160)} />
        <meta name="twitter:image" content={image} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
    </>
  );
}
